/**
 * Deterministic relationship discovery review (ADR-0026 §4, $0 AI).
 *
 * Runs the backend's introspection (real FK constraints + app relation columns +
 * a name heuristic), lists the candidates with their source, and bulk-creates the
 * ones the operator keeps. Exact sources (fk / app_relation) default checked; the
 * fuzzy heuristic defaults UNchecked — review before trusting.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2, Sparkles } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CARDINALITY_GLYPH,
  relationshipsApi,
  type RelationshipCandidate,
} from "@/lib/api-relationships";

const SOURCE_TONE: Record<RelationshipCandidate["source"], "active" | "neutral" | "warning"> = {
  fk: "active",
  app_relation: "active",
  value_overlap: "active",
  heuristic: "warning",
  ai: "active", // deterministic discovery never emits "ai"; kept for type completeness
};

function candKey(c: RelationshipCandidate): string {
  return `${c.from_dataset_id}:${c.from_field}->${c.to_dataset_id}:${c.to_field}`;
}

export function DiscoverRelationshipsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [picked, setPicked] = React.useState<Set<string>>(() => new Set());

  const discoverQ = useQuery({
    queryKey: ["relationships", "discover"],
    queryFn: () => relationshipsApi.discover(),
    enabled: open,
    gcTime: 0,
  });
  const candidates = discoverQ.data?.candidates ?? [];

  // Default-select the exact sources once results arrive.
  React.useEffect(() => {
    if (discoverQ.data) {
      setPicked(new Set(candidates.filter((c) => c.source !== "heuristic").map(candKey)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discoverQ.data]);

  const create = useMutation({
    mutationFn: async () => {
      const chosen = candidates.filter((c) => picked.has(candKey(c)));
      await Promise.all(
        chosen.map((c) =>
          relationshipsApi.create({
            name: c.name,
            from_dataset_id: c.from_dataset_id,
            from_field: c.from_field,
            to_dataset_id: c.to_dataset_id,
            to_field: c.to_field,
            cardinality: c.cardinality,
          }),
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["relationships"] });
      onOpenChange(false);
    },
  });

  const toggle = (key: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm font-semibold">
            <Sparkles className="h-4 w-4" strokeWidth={1.5} />
            {t("relationships.discover.title", "Scopri relazioni")}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="mb-3 text-xs text-muted-foreground">
            {t(
              "relationships.discover.body",
              "Da vincoli FK reali, colonne-relazione delle app ed euristica sui nomi. Nessun costo AI — rivedi e crea.",
            )}
          </p>
          {discoverQ.isLoading ? (
            <div className="flex items-center gap-2 py-8 text-xs text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              {t("relationships.discover.scanning", "Analisi sorgenti…")}
            </div>
          ) : candidates.length === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              {t("relationships.discover.none", "Nessuna nuova relazione trovata.")}
            </p>
          ) : (
            <ul className="max-h-[55vh] divide-y divide-border/60 overflow-auto rounded-lg border border-border/60">
              {candidates.map((c) => {
                const key = candKey(c);
                return (
                  <li key={key} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={picked.has(key)}
                      onChange={() => toggle(key)}
                      className="h-3.5 w-3.5 accent-[hsl(var(--accent))]"
                    />
                    <span className="truncate">
                      <span className="text-muted-foreground">{c.from_dataset_name}</span>
                      <span className="font-mono text-xs">.{c.from_field}</span>
                    </span>
                    <Badge tone="neutral" className="text-[10px]">
                      {CARDINALITY_GLYPH[c.cardinality]}
                    </Badge>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
                    <span className="truncate">
                      <span className="text-muted-foreground">{c.to_dataset_name}</span>
                      <span className="font-mono text-xs">.{c.to_field}</span>
                    </span>
                    <Badge tone={SOURCE_TONE[c.source]} className="ml-auto text-[10px]">
                      {t(`relationships.source.${c.source}`, c.source)}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
          {create.isError && (
            <p className="mt-2 text-xs text-danger">{(create.error as Error).message}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button
            size="sm"
            disabled={picked.size === 0 || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />}
            {t("relationships.discover.create", "Crea {{n}}", { n: picked.size })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
